// Pure geometry helpers for placing/auto-hiding hotspots in the 360 viewer.
//
// The track is parametrized in *frame space*: each sample is keyed by `df`,
// the signed frame delta from the hotspot's anchor frame, wrapped across the
// 0/N seam. We deliberately avoid an angle parametrization because the video
// rotates non-uniformly (eased diffusion sub-clips) and the projected motion
// is non-uniform in angle (fast facing the camera, slow at the silhouette).
// Anchoring positions to real frames captures both, so the viewer just maps
// `currentTime -> frame` and interpolates by `df`.

import type { HotspotSample, HotspotTrack, HotspotsVideoMeta } from '../types';

/** Signed frame delta wrapped into [-N/2, N/2] (matches the backend). */
export function wrapFrameDelta(delta: number, frameCount: number): number {
  if (frameCount <= 0) return delta;
  const n = frameCount;
  return ((((delta + n / 2) % n) + n) % n) - n / 2;
}

/** Interpolate a sample position at anchor-relative frame delta `df`. */
export function sampleAt(samples: HotspotSample[], df: number): { x: number; y: number } | null {
  if (samples.length === 0) return null;
  if (df <= samples[0].df) return { x: samples[0].x, y: samples[0].y };
  const lastIdx = samples.length - 1;
  if (df >= samples[lastIdx].df) return { x: samples[lastIdx].x, y: samples[lastIdx].y };
  for (let i = 1; i < samples.length; i += 1) {
    if (df <= samples[i].df) {
      const a = samples[i - 1];
      const b = samples[i];
      const span = Math.max(1e-6, b.df - a.df);
      const f = (df - a.df) / span;
      return { x: a.x + f * (b.x - a.x), y: a.y + f * (b.y - a.y) };
    }
  }
  const last = samples[lastIdx];
  return { x: last.x, y: last.y };
}

export interface HotspotPlacement {
  /** Normalized position within the rendered video content box. */
  x: number;
  y: number;
  /** 0..1; <=0 means hidden. Fades near the visible-arc edges. */
  opacity: number;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * Resolve a hotspot's placement at video time `t`. Returns `null` when the
 * hotspot is outside its visible arc (auto-hidden). `fadeFrames` smooths the
 * appear/disappear edges to avoid popping.
 */
export function placeHotspot(
  track: HotspotTrack,
  t: number,
  video: HotspotsVideoMeta,
  fadeFrames = 3,
): HotspotPlacement | null {
  const frame = t * video.fps;
  const df = wrapFrameDelta(frame - track.anchor_frame, video.frame_count);
  const { min_df, max_df } = track.visible;
  if (df < min_df || df > max_df) return null;
  const pos = sampleAt(track.samples, df);
  if (!pos) return null;
  const fade = Math.max(1e-6, fadeFrames);
  const opacity = clamp01(Math.min((df - min_df) / fade, (max_df - df) / fade));
  if (opacity <= 0) return null;
  return { x: pos.x, y: pos.y, opacity };
}

/** Content rect of an object-contain <video> within its element box. */
export function videoContentRect(video: HTMLVideoElement): {
  offsetX: number;
  offsetY: number;
  contentW: number;
  contentH: number;
} | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const ew = video.clientWidth;
  const eh = video.clientHeight;
  if (!vw || !vh || !ew || !eh) return null;
  const scale = Math.min(ew / vw, eh / vh);
  const contentW = vw * scale;
  const contentH = vh * scale;
  return {
    offsetX: (ew - contentW) / 2,
    offsetY: (eh - contentH) / 2,
    contentW,
    contentH,
  };
}
